import { useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { useSnackbar } from 'notistack';

import DialogHeader from 'components/DialogHeader';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoInterface,
  atendimentosSelector,
  setAtendenteSelecionadoV2,
  transferirAtendimento,
} from 'store/reducers/atendimentos';
import { EquipeInterface, EquipeInterfaceV2 } from 'store/reducers/equipes';
import { UsuarioInterfaceV2 } from 'store/reducers/usuarios';

import ListaAtendentes from './components/ListaAtendentes';
import ListaEquipes from './components/ListaEquipes';
import OpcoesTransferencia, {
  OpcoesTransferenciaType,
} from './components/OpcoesTransferencia';
import SelectEquipeAtendente from './components/SelectEquipeAtendente';

const useStyles = makeStyles(({ typography, palette, spacing }) => ({
  input: {
    fontSize: typography.pxToRem(14),
  },
  alertBox: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    backgroundColor: palette.type === 'dark' ? '#1e1e1e' : '#f5f5f5',
    border: `1px solid ${palette.type === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: spacing(2),
  },
  alertIcon: {
    marginTop: 2,
    fontSize: 18,
    color: palette.type === 'dark' ? '#9e9e9e' : '#616161',
    flexShrink: 0,
  },
  alertTitle: {
    fontSize: typography.pxToRem(12),
    fontWeight: 600,
    color: palette.type === 'dark' ? '#e0e0e0' : '#212121',
    marginBottom: 2,
  },
  alertBody: {
    fontSize: typography.pxToRem(12),
    color: palette.type === 'dark' ? '#9e9e9e' : '#616161',
    lineHeight: 1.5,
  },
  commentLabel: {
    fontSize: typography.pxToRem(12),
    fontWeight: 600,
    marginBottom: spacing(0.75),
    marginTop: spacing(2),
    color: palette.text.secondary,
    '& span': {
      fontWeight: 400,
    },
  },
}));

type ModalTransferirAtendimentoProps = {
  open: boolean;
  atendimento?: AtendimentoInterface;
  onClose: () => void;
};

const TOTAL_STEPS = 4;
const MAX_COMMENT_LENGTH = 5000;

export default function ModalTransferirAtendimento(
  props: ModalTransferirAtendimentoProps,
) {
  const { open, onClose, atendimento } = props;
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { loadingTransferirAtendimento } = useAppSelector(atendimentosSelector);
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(1);
  const [comentario, setComentario] = useState('');
  const [progress, setProgress] = useState(TOTAL_STEPS * 10);
  const [equipeSelected, setEquipeSelected] = useState<EquipeInterface>();
  const [atendenteSelected, setAtendenteSelected] =
    useState<UsuarioInterfaceV2>();
  const [opcaoTransferencia, setOpcaoTransferencia] =
    useState<OpcoesTransferenciaType>();

  const [disabledButton, setDisabledButton] = useState(false);

  // Equipe do atendente selecionado — obrigatória na transferência direta para ATENDENTE
  const [equipeDoAtendente, setEquipeDoAtendente] =
    useState<EquipeInterfaceV2>();

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setProgress(prevProgress => Math.min(prevProgress + TOTAL_STEPS * 10, 100));
  };

  const handleBack = () => {
    setComentario('');
    setEquipeDoAtendente(undefined);
    setActiveStep(prevActiveStep => prevActiveStep - 1);
    setProgress(prevProgress => Math.min(prevProgress - TOTAL_STEPS * 10, 100));
  };

  const handleSelectOpcaoTransferencia = (opcao: OpcoesTransferenciaType) => {
    setOpcaoTransferencia(opcao);
    handleNext();
  };

  const handleSelectEquipe = (equipe: EquipeInterface) => {
    setEquipeSelected(equipe);
    handleNext();
  };

  const handleSelectAtendente = (atendente: UsuarioInterfaceV2) => {
    setAtendenteSelected(atendente);
    setEquipeDoAtendente(undefined); // limpa equipe ao trocar de atendente
    dispatch(setAtendenteSelecionadoV2(atendente));
    handleNext();
  };

  const handleTransferir = () => {
    if (!atendimento) return;
    if (opcaoTransferencia === 'EQUIPE' && !equipeSelected) return;
    if (opcaoTransferencia === 'ATENDENTE' && !atendenteSelected) return;
    if (opcaoTransferencia === 'ATENDENTE' && !equipeDoAtendente) return;

    executeTransferir();
  };

  const executeTransferir = () => {
    if (!atendimento) return;

    setDisabledButton(true);

    const params = {
      atendimentoId: atendimento.id,
      transferTo: opcaoTransferencia,
      teamId:
        opcaoTransferencia === 'ATENDENTE'
          ? equipeDoAtendente?.id // equipe obrigatória ao transferir para atendente
          : equipeSelected?.id,
      atendenteId: atendenteSelected?.id,
      comment: comentario,
    };

    /**
     * Transferência direta (sem aceite):
     * O back-end efetiva a transferência imediatamente. O atendimento é
     * removido da lista do remetente pelo reducer (transferirAtendimento.fulfilled).
     * O comentário, se preenchido, é registrado pelo back-end como tipo=comentario
     * na timeline interna — sem envio ao contato.
     */
    dispatch(transferirAtendimento(params))
      .unwrap()
      .then(({ params }) => {
        if (params.transferTo === 'EQUIPE') {
          enqueueSnackbar('O atendimento foi transferido para a equipe');
        } else {
          enqueueSnackbar(
            `O atendimento foi transferido para ${atendenteSelected?.name}`,
          );
        }
        onClose();
      })
      .catch(error => {
        if (error.response) {
          enqueueSnackbar(error.response.data.message);
        }
        setDisabledButton(false);
      });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      TransitionProps={{
        onExited: () => {
          setActiveStep(1);
          setComentario('');
          setProgress(TOTAL_STEPS * 10);
          setDisabledButton(false);
          setEquipeDoAtendente(undefined);
        },
      }}
    >
      <DialogHeader
        title="Transferir atendimento"
        onBack={activeStep > 1 ? handleBack : undefined}
        onClose={onClose}
      />

      <DialogContent style={{ paddingTop: 0 }}>
        {activeStep === 1 && (
          <OpcoesTransferencia onSelect={handleSelectOpcaoTransferencia} />
        )}

        {activeStep === 2 && opcaoTransferencia === 'EQUIPE' && (
          <ListaEquipes onSelect={handleSelectEquipe} />
        )}

        {activeStep === 2 && opcaoTransferencia === 'ATENDENTE' && (
          <ListaAtendentes onSelect={handleSelectAtendente} />
        )}

        {activeStep === 3 && (
          <>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Avatar>
                  <SwapHorizIcon />
                </Avatar>
              </ListItemAvatar>

              {opcaoTransferencia === 'EQUIPE' && (
                <ListItemText
                  primary="Você está transferindo para a equipe"
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondary={equipeSelected?.nome}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    style: { fontWeight: 600 },
                  }}
                />
              )}

              {opcaoTransferencia === 'ATENDENTE' && (
                <ListItemText
                  style={{ padding: 0 }}
                  primary="Você está transferindo para o atendente"
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondary={atendenteSelected?.name}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    style: { fontWeight: 600 },
                  }}
                />
              )}
            </ListItem>

            {/* Alerta de transferência direta — exibido logo após o resumo */}
            {opcaoTransferencia === 'ATENDENTE' && (
              <div className={classes.alertBox}>
                <CheckCircleOutlineIcon className={classes.alertIcon} />
                <div>
                  <div className={classes.alertTitle}>
                    Transferência direta para o atendente
                  </div>
                  <div className={classes.alertBody}>
                    O atendimento será enviado diretamente para{' '}
                    <strong>{atendenteSelected?.name}</strong>. Ele não precisa
                    mais confirmar o recebimento — a conversa já aparece na fila
                    dele automaticamente.
                  </div>
                </div>
              </div>
            )}

            {/* Seleção de equipe do atendente — obrigatório ao transferir para ATENDENTE */}
            {opcaoTransferencia === 'ATENDENTE' && atendenteSelected && (
              <>
                <SelectEquipeAtendente
                  userId={atendenteSelected.id}
                  selectedId={equipeDoAtendente?.id}
                  onSelect={setEquipeDoAtendente}
                />
              </>
            )}

            <Typography className={classes.commentLabel}>
              Comentário <span>(opcional)</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              inputProps={{
                className: classes.input,
                maxLength: MAX_COMMENT_LENGTH,
              }}
              value={comentario}
              onChange={event => setComentario(event.target.value)}
              variant="outlined"
              placeholder="Adicione um contexto sobre este atendimento para o atendente..."
            />
          </>
        )}

        {activeStep !== 3 && <Box style={{ height: 16 }} />}
      </DialogContent>

      {activeStep === 3 && (
        <DialogActions>
          <Button onClick={onClose} disabled={disabledButton}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTransferir}
            disabled={
              disabledButton ||
              // Bloqueia se for transferência para ATENDENTE sem equipe selecionada
              (opcaoTransferencia === 'ATENDENTE' && !equipeDoAtendente)
            }
          >
            Transferir
          </Button>
        </DialogActions>
      )}

      <LinearProgress
        variant={loadingTransferirAtendimento ? 'indeterminate' : 'determinate'}
        value={progress}
      />
    </Dialog>
  );
}
